import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from '../i18n'
import type { Project } from '../types'
import { apiPost, apiDelete } from '../hooks/useApi'

interface ProjectListProps {
  projects: Project[]
  loading: boolean
  selectedId?: string
  onProjectCreated: () => void
}

export function ProjectList({ projects, loading, selectedId, onProjectCreated }: ProjectListProps) {
  const { t } = useTranslation()
  const [showForm, setShowForm] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return

    setCreating(true)
    setError(null)
    try {
      await apiPost('/projects', { name: name.trim(), description: description.trim() || null })
      setName('')
      setDescription('')
      setShowForm(false)
      onProjectCreated()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.failedToCreateProject'))
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (projectId: string) => {
    if (!confirm(t('confirm.deleteProject'))) return

    try {
      await apiDelete(`/projects/${projectId}`)
      onProjectCreated()
    } catch (err) {
      alert(err instanceof Error ? err.message : t('error.failedToDeleteProject'))
    }
  }

  if (loading) {
    return (
      <div className="card" data-testid="project-list">
        <h2 className="card-title">{t('page.projects')}</h2>
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-gray-400)' }}>
          {t('label.loading')}
        </div>
      </div>
    )
  }

  return (
    <div className="card" data-testid="project-list">
      <h2 className="card-title">{t('page.projects')}</h2>

      {showForm ? (
        <form onSubmit={handleSubmit} style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            placeholder={t('label.projectName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input"
            style={{ marginBottom: '0.5rem' }}
            data-testid="project-name-input"
            autoFocus
          />
          <input
            type="text"
            placeholder={t('label.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="input"
            style={{ marginBottom: '0.5rem' }}
            data-testid="project-description-input"
          />
          {error && (
            <div style={{ color: 'var(--color-error)', marginBottom: '0.5rem' }}>{error}</div>
          )}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={creating || !name.trim()}
              aria-label={t('button.create')}
              title={t('button.create')}
            >
              <i className="pi pi-check" aria-hidden="true" style={{ marginRight: '0.25rem' }} />
              {creating ? t('misc.creating') : t('button.create')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowForm(false)}
              disabled={creating}
              aria-label={t('button.cancel')}
              title={t('button.cancel')}
            >
              <i className="pi pi-times" aria-hidden="true" style={{ marginRight: '0.25rem' }} />
              {t('button.cancel')}
            </button>
          </div>
        </form>
      ) : (
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
          data-testid="create-project-btn"
          aria-label={t('button.newProject')}
          title={t('button.newProject')}
        >
          <i className="pi pi-plus" aria-hidden="true" style={{ marginRight: '0.25rem' }} />
          {t('button.newProject')}
        </button>
      )}

      {projects.length === 0 ? (
        <div
          style={{
            marginTop: '1rem',
            color: 'var(--color-gray-400)',
            textAlign: 'center',
            padding: '2rem',
          }}
        >
          {t('empty.noProjects')}
        </div>
      ) : (
        <ul className="project-list" style={{ marginTop: '1rem', listStyle: 'none', padding: 0 }}>
          {projects.map((project) => (
            <li
              key={project.id}
              className={`project-item ${selectedId === project.id ? 'selected' : ''}`}
              style={{
                padding: '0.75rem',
                borderRadius: '4px',
                marginBottom: '0.5rem',
                backgroundColor:
                  selectedId === project.id ? 'var(--color-primary-dark)' : 'transparent',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Link
                to={`/config/projects/${project.id}`}
                style={{
                  color: 'inherit',
                  textDecoration: 'none',
                  flex: 1,
                }}
                data-testid={`project-link-${project.id}`}
              >
                <div style={{ fontWeight: 500 }}>{project.name}</div>
                {project.description && (
                  <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-400)' }}>
                    {project.description}
                  </div>
                )}
              </Link>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => handleDelete(project.id)}
                aria-label={t('button.delete')}
                title={t('button.delete')}
                style={{ marginLeft: '0.5rem' }}
              >
                <i className="pi pi-trash" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
